const createHiddenAnchor = (href, fileName) => {
  if (typeof document === "undefined") {
    return null;
  }

  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = fileName || "download";
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  return anchor;
};

export const downloadBlob = (blob, fileName = "download") => {
  if (!(blob instanceof Blob)) {
    return false;
  }

  if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
    console.error("Blob download is not supported in this environment");
    return false;
  }

  try {
    const url = URL.createObjectURL(blob);
    createHiddenAnchor(url, fileName || "download");
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 0);
    return true;
  } catch (error) {
    console.error("Failed to trigger file download", error);
  }

  return false;
};

const base64ToUint8 = (base64) => {
  if (typeof base64 !== "string") {
    return null;
  }

  const trimmed = base64.trim();

  if (!trimmed) {
    return null;
  }

  if (typeof Buffer === "function" && Buffer.from) {
    try {
      return Uint8Array.from(Buffer.from(trimmed, "base64"));
    } catch (error) {
      console.error("Failed to decode base64 file content", error);
    }
  }

  if (typeof atob === "function") {
    const binary = atob(trimmed);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }

    return bytes;
  }

  console.error("Base64 decoding is not supported in this environment");
  return null;
};

export const downloadBase64 = (
  base64,
  fileName = "download",
  mimeType = "application/octet-stream"
) => {
  const byteArray = base64ToUint8(base64);

  if (byteArray?.length && typeof Blob === "function") {
    const blob = new Blob([byteArray], { type: mimeType });
    if (downloadBlob(blob, fileName)) {
      return true;
    }
  }

  if (typeof document === "undefined") {
    return false;
  }

  try {
    const dataUrl = `data:${mimeType};base64,${base64}`;
    createHiddenAnchor(dataUrl, fileName || "download");
    return true;
  } catch (error) {
    console.error("Failed to trigger base64 file download", error);
  }

  return false;
};

export const downloadFilePayload = ({
  blob,
  base64,
  fileName = "download",
  mimeType = "application/octet-stream",
} = {}) => {
  if (blob instanceof Blob && downloadBlob(blob, fileName)) {
    return true;
  }

  if (typeof base64 === "string" && base64) {
    return downloadBase64(base64, fileName, mimeType);
  }

  console.error("No downloadable data was provided");
  return false;
};

export default downloadBlob;
